import WorkerApiType, { CurrentUserResult, SignInArguments, TerminalResult, TerminalArguments, AddOrUpdateTerminalAreaArguments, AddOrUpdateTerminalAreaResult, UpdateTerminalMapViewArguments, UpdateTerminalMapViewResult, DeleteTerminalAreaArguments, DeleteTerminalAreaResult } from './worker-api-type';
import urljoin from 'url-join';

import { HacpackQueryResponse, HacpackMutationResponse, Terminal, SignInResult, SignOutResult } from '../types';

const baseUrl = process.env.REACT_APP_PUBLIC_URL ?? '/.';
const graphQlUrl = urljoin(baseUrl, 'graph');
//let language = process.env.REACT_APP_DEFAULT_LANGUAGE;

const currentUserQuery = `query {
    currentUser {
        id
        login
        displayName
    }
}`;

const signInMutation = `mutation ($credentials: SignInInput!) {
    signIn (credentials: $credentials)  {
        isOk
        data {
            id
            login
            displayName
        }
    }
}`;

const signOutMutation = `mutation {
    signOut {
        isOk
    }
}`;

const terminalQuery = `query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
        port
        maxCei
        maxFei
        maxRadius
        teusLimit
        dataUpdate {
            id
            lastUpdate
        }
        riskAssessment {
            id
            maxFei
            maxCei
            radius
        }
        segregations {
            id
            loadingUnit1Identifier
            loadingUnit2Identifier
            class1
            class2
            row1
            row2
            bay1
            bay2
            area
            terminalName
            dateOfInput
        }
        handlings { 
            id 
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            type
            fei
            cei
            standardDistance
            dangerousGoods {
                id
            }
        }
    }
}
`;

const updateTerminalMapViewMutation = `mutation ($terminalMapView:TerminalMapViewInput!) {
  updateTerminalMapView(terminalMapView: $terminalMapView) {
    id
    centerLatitude
    centerLongitude
    mapZoom
    northLatitude
    southLatitude
    westLongitude
    eastLongitude
  }
}`;

const addTerminalAreaMutation = `mutation ($terminalArea:TerminalAreaInput!) {
  addTerminalArea(terminalArea: $terminalArea) {
    id
    terminalId
    name
    centerLatitude
    centerLongitude
  }
}`;

const updateTerminalAreaMutation = `mutation ($terminalArea:TerminalAreaInput!) {
  updateTerminalArea(terminalArea: $terminalArea) {
    id
    terminalId
    name
    centerLatitude
    centerLongitude
  }
}`;

const deleteTerminalAreaMutation = `mutation ($terminalAreaId: Int!) {
  deleteTerminalArea(terminalAreaId: $terminalAreaId) {
    isOk
  }
}`;

const WorkerApi: WorkerApiType = {

    setLanguage(lng: string): Promise<void> {
        //language = lng;
        return Promise.resolve();
    },

    query<TValue,TVars> (data: { query: string; variables?: TVars; }): Promise<TValue> {
        return new Promise<TValue>(async (resolve, reject) => {
            try {
                const body = JSON.stringify(data);

                const response = await fetch(graphQlUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body
                });

                response.json().then(response => {
                    resolve(response.data as TValue)
                }).catch(reject);
            } catch(e) {
                reject(e);
            }
        });
    },

    currentUser (): Promise<CurrentUserResult> {
        return new Promise<CurrentUserResult>(async (resolve, reject) => {
            this.query<HacpackQueryResponse>({ query: currentUserQuery }).then(data => {
                resolve(data.currentUser??null)
            }).catch(reject);
        });
    },

    signIn({ credentials }:SignInArguments) : Promise<SignInResult> {
        return new Promise<SignInResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse, SignInArguments>({ query: signInMutation, variables: { credentials } }).then(data => {
                resolve(data.signIn as SignInResult)
            }).catch(reject);
        });
    },

    signOut(): Promise<SignOutResult> {
        return new Promise<SignOutResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse,SignOutResult>({ query: signOutMutation }).then(data => {
                resolve(data.signOut as SignOutResult)
            }).catch(reject);
        });
    },

    terminal({ id }: TerminalArguments): Promise<TerminalResult> {
        return new Promise<TerminalResult>(async (resolve, reject) => {
            this.query<HacpackQueryResponse,TerminalArguments>({ query: terminalQuery, variables: {id} }).then(data => {
                if ((data.terminal??null)===null) {
                    reject("Notfound");
                } else {
                    resolve(data.terminal as Terminal);
                }
            }).catch(reject);
        });
    },

    updateTerminalMapView({ terminalMapView }: UpdateTerminalMapViewArguments): Promise<UpdateTerminalMapViewResult> {
        return new Promise<UpdateTerminalMapViewResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse, UpdateTerminalMapViewArguments>({ query: updateTerminalMapViewMutation, variables: { terminalMapView } }).then(data => {
                resolve(data.updateTerminalMapView as UpdateTerminalMapViewResult);
            }).catch(reject);
        });
    },

    addTerminalArea({ terminalArea }: AddOrUpdateTerminalAreaArguments): Promise<AddOrUpdateTerminalAreaResult> {
        return new Promise<AddOrUpdateTerminalAreaResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse, AddOrUpdateTerminalAreaArguments>({ query: addTerminalAreaMutation, variables: { terminalArea } }).then(data => {
                resolve(data.addTerminalArea as AddOrUpdateTerminalAreaResult);
            }).catch(reject);
        });
    },

    updateTerminalArea({ terminalArea }: AddOrUpdateTerminalAreaArguments): Promise<AddOrUpdateTerminalAreaResult> {
        return new Promise<AddOrUpdateTerminalAreaResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse, AddOrUpdateTerminalAreaArguments>({ query: updateTerminalAreaMutation, variables: { terminalArea } }).then(data => {
                resolve(data.updateTerminalArea as AddOrUpdateTerminalAreaResult);
            }).catch(reject);
        });
    },

    deleteTerminalArea({ terminalAreaId }: DeleteTerminalAreaArguments): Promise<DeleteTerminalAreaResult> {
        return new Promise<DeleteTerminalAreaResult>(async (resolve, reject) => {
            this.query<HacpackMutationResponse, DeleteTerminalAreaArguments>({ query: deleteTerminalAreaMutation, variables: { terminalAreaId } }).then(data => {
                resolve(data.deleteTerminalArea as DeleteTerminalAreaResult);
            }).catch(reject);
        });
    }
};

export default WorkerApi;